<template>
  <header class="bg-white shadow-sm p-4 flex justify-between items-center">
    <div class="flex items-center">
      <button 
        v-if="showMenuToggle"
        @click="$emit('toggle-sidebar')" 
        class="mr-4 text-gray-700 hover:text-primary-700 md:hidden"
      >
        <i class="fas fa-bars text-xl"></i>
      </button>
      <h1 class="text-xl font-bold text-gray-800">{{ title }}</h1>
    </div>
    
    <div class="flex items-center space-x-4">
      <!-- Search -->
      <div v-if="showSearch" class="relative hidden md:block">
        <input 
          type="text" 
          placeholder="Search..." 
          class="pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          v-model="searchQuery"
          @input="$emit('search', searchQuery)"
        >
        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
      </div>
      
      <!-- User menu - only shown when authenticated -->
      <div v-if="authStore.isAuthenticated" class="relative" ref="userMenuRef">
        <button 
          @click="toggleUserMenu" 
          class="flex items-center space-x-2 focus:outline-none"
        >
          <div class="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white">
            {{ userInitials }}
          </div>
          <span class="hidden md:inline">{{ userFirstName }}</span>
          <i class="fas fa-chevron-down text-sm"></i>
        </button>
        
        <!-- Dropdown menu -->
        <div 
          v-if="userMenuOpen" 
          class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-20"
        >
          <router-link 
            to="/dashboard" 
            class="block px-4 py-2 text-gray-700 hover:bg-primary-50"
          >
            Dashboard
          </router-link>
          <router-link 
            to="/properties" 
            class="block px-4 py-2 text-gray-700 hover:bg-primary-50"
          >
            Properties
          </router-link>
          <router-link 
            to="/analytics" 
            class="block px-4 py-2 text-gray-700 hover:bg-primary-50"
          >
            Analytics
          </router-link>
          <div class="border-t my-1"></div>
          <button 
            @click="logout" 
            class="block w-full text-left px-4 py-2 text-gray-700 hover:bg-primary-50"
          >
            Logout
          </button>
        </div>
      </div>
      
      <!-- Guest menu - only shown when not authenticated -->
      <div v-else class="flex items-center space-x-2">
        <router-link 
          to="/login" 
          class="px-4 py-2 rounded-lg border border-primary-600 text-primary-600 hover:bg-primary-50"
        >
          Login
        </router-link>
        <router-link 
          to="/signup" 
          class="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700"
        >
          Sign Up
        </router-link>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, defineEmits, computed } from 'vue';
import { useAuthStore } from '../stores/auth';

const props = defineProps({
  title: {
    type: String,
    default: 'GeoProperty Hub'
  },
  showSearch: {
    type: Boolean,
    default: false
  },
  showMenuToggle: {
    type: Boolean,
    default: false
  }
});

defineEmits(['toggle-sidebar', 'search']);

const authStore = useAuthStore();
const userMenuOpen = ref(false);
const userMenuRef = ref(null);
const searchQuery = ref('');

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value;
};

const handleClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    userMenuOpen.value = false;
  }
};

const logout = () => {
  authStore.logout();
  userMenuOpen.value = false;
};

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

// Computed properties to safely handle user data
const userInitials = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  
  // Handle case when user data is nested in a 'user' property
  const userData = user.user || user;
  
  const firstName = userData.first_name || '';
  const lastName = userData.last_name || '';
  
  return `${firstName.charAt(0)}${lastName.charAt(0)}`;
});

const userFirstName = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  
  // Handle case when user data is nested in a 'user' property
  const userData = user.user || user;
  
  return userData.first_name || '';
});
</script>
